import type { KeyboardEvent } from "react";

/**
 * Excel-aktig grid-navigasjon for admin-timer-tabellene. Alle navigerbare
 * elementer (editerbare celler + action-knapper) markeres med
 * `data-nav-cell="true"` og en `data-col`-streng som identifiserer kolonnen
 * for vertikal navigasjon.
 *
 * Horisontal (next/prev): DOM-rekkefølge — Tab/Shift+Tab + ArrowLeft/Right.
 * Vertikal (down/up): hopper til neste celle med samme `data-col` i DOM-
 * rekkefølge. Cellene trenger ikke å være kolonneutlignet i en streng grid;
 * kolonnenavnet styrer mappingen, ikke posisjon.
 */
export type NavDirection = "next" | "prev" | "down" | "up";

const NAV_SELECTOR = "[data-nav-cell='true']";

export function findNavTarget(
  current: HTMLElement | null,
  dir: NavDirection,
): HTMLElement | undefined {
  if (!current) return undefined;
  const all = Array.from(document.querySelectorAll<HTMLElement>(NAV_SELECTOR));
  const idx = all.indexOf(current);
  if (idx === -1) return undefined;

  if (dir === "next") return all[idx + 1];
  if (dir === "prev") return all[idx - 1];

  const col = current.getAttribute("data-col");
  if (!col) return undefined;
  const sameCol = all.filter((el) => el.getAttribute("data-col") === col);
  const colIdx = sameCol.indexOf(current);
  return dir === "down" ? sameCol[colIdx + 1] : sameCol[colIdx - 1];
}

/**
 * Mapper en KeyboardEvent til navigasjonsretning. Returnerer null hvis tasten
 * ikke skal trigge navigasjon. Tab/Shift+Tab og piltastene støttes.
 */
export function arrowKeyToDirection(e: KeyboardEvent): NavDirection | null {
  switch (e.key) {
    case "ArrowRight":
      return "next";
    case "ArrowLeft":
      return "prev";
    case "ArrowDown":
      return "down";
    case "ArrowUp":
      return "up";
    case "Tab":
      return e.shiftKey ? "prev" : "next";
    default:
      return null;
  }
}

/**
 * Returnerer true hvis tasten er en "printbar" karakter som skal trigge
 * edit-mode (Excel-aktig: skriv et bokstav på en valgt celle og editeren
 * åpnes med karakteren som initial-verdi). Modifier-taster (Ctrl/Cmd/Alt)
 * teller ikke.
 */
export function isPrintableKey(e: KeyboardEvent): boolean {
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  return e.key.length === 1;
}
